import { PlusCircleIcon, Tally2Icon } from 'lucide-react';

import { cn } from '@/lib/utils';

import { ASSIGNMENT_BASE_OFFSET_REM, ASSIGNMENT_LEVEL_SPACING_REM } from '../../constants';

interface PhantomAssignmentProps {
  startOffset: number;
  endOffset: number;
  isVisible: boolean;
  level: number;
  isDragging?: boolean;
  isCompact: boolean;
  /** Current cell width in rem — a single-cell selection has endOffset === cellWidth. */
  cellWidth: number;
}

const PLUS_CIRCLE_ICON_SIZE = 12;
const TALLY_2_ICON_SIZE = 10;

/**
 * This component represent visual interaction with a timeline
 * like hovering and dragging to open create form with prefilled dates
 */
export const PhantomAssignment = ({
  startOffset,
  endOffset,
  isVisible,
  level,
  isCompact,
  cellWidth,
  isDragging = false,
}: PhantomAssignmentProps) => {
  const topPosition = level * ASSIGNMENT_LEVEL_SPACING_REM + ASSIGNMENT_BASE_OFFSET_REM;

  const isSingleCellSelected = endOffset === cellWidth;

  if (!isVisible) {
    return null;
  }

  const isSecondIconShown = isDragging && !isSingleCellSelected;

  return (
    <div
      id="phantom-assignment"
      className={cn(
        'absolute z-[3] flex h-7 w-7 items-center rounded-[100px] p-2 transition-opacity duration-150',
        'bg-accent',
        {
          'pointer-events-none': !isDragging,
          'pointer-events-auto cursor-grabbing': isDragging,
          'p-[3px]': isCompact,
        }
      )}
      style={{
        left: `${startOffset}rem`,
        width: `${endOffset}rem`,
        top: `${topPosition}rem`,
      }}
    >
      {/* The "+" is centered within the FIRST cell (cellWidth) regardless of how many
          cells the phantom spans during drag. The negative margin cancels the
          container's left padding so the box aligns to the true cell edge, letting
          justify-center center the icon in the cell. The drag-end indicator (when
          dragging across cells) is pinned to the right edge. */}
      <div
        className={cn(
          'sticky left-0 top-0 flex shrink-0 items-center justify-center text-[10px] font-medium text-primary',
          isCompact ? '-ml-[3px]' : '-ml-2'
        )}
        style={{ width: `${cellWidth}rem` }}
      >
        <PlusCircleIcon size={PLUS_CIRCLE_ICON_SIZE} />
      </div>
      {isSecondIconShown && (
        <div className="ml-auto flex items-center text-[10px] font-medium">
          <Tally2Icon size={TALLY_2_ICON_SIZE} className="text-muted-foreground" />
        </div>
      )}
    </div>
  );
};
